/**
 * Parse franchise price-list dual prices into selectable options.
 * Examples: "500 | 700", "₹1,200 / 1,500", names with (M|L) or M/L.
 */

export function formatDisplayPrice(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return '';
  const n = Number(amount);
  if (Number.isInteger(n)) return `₹${n.toLocaleString('en-IN')}`;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function toNumberOrNull(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function isPriceVariantRow(row) {
  if (!row) return false;
  if (row.option_key) return true;
  if (row.base_service_id) return true;
  return String(row.id || '').includes('::');
}

function variantPayableAmount(row) {
  if (!isPriceVariantRow(row)) return null;
  return toNumberOrNull(row?.amount) ?? toNumberOrNull(row?.price);
}

export function serviceHasDiscount(service) {
  const d = toNumberOrNull(service?.discount);
  return d != null && d > 0 && toNumberOrNull(service?.net_price) != null;
}

function saleFromMrpAndDiscount(mrp, discountPct) {
  const m = toNumberOrNull(mrp);
  const d = toNumberOrNull(discountPct);
  if (m == null) return null;
  if (d == null || d <= 0) return m;
  return Math.round((m - (m * d) / 100) * 100) / 100;
}

/**
 * @returns {{ kind: 'single' | 'sale', mrp: number, sale?: number } | null}
 */
export function resolveCatalogPriceDisplay(row, catalogService) {
  const variantAmt = variantPayableAmount(row);
  if (variantAmt != null) {
    return { kind: 'single', mrp: variantAmt };
  }

  const base = catalogService || row;
  const discount = toNumberOrNull(base?.discount ?? row?.discount);
  const optionAmount = toNumberOrNull(row?.amount);
  let mrp = toNumberOrNull(row?.net_price ?? base?.net_price);
  if (mrp == null && optionAmount != null) mrp = optionAmount;
  if (mrp == null) {
    const options = parseServicePriceOptions(row || base);
    if (options.length === 1) mrp = options[0].amount;
  }
  if (mrp == null) return null;

  let sale = toNumberOrNull(row?.sale_price ?? base?.sale_price);
  if (sale == null) sale = saleFromMrpAndDiscount(mrp, discount);
  if (discount != null && discount > 0 && sale != null && sale < mrp) {
    return { kind: 'sale', mrp, sale };
  }
  return { kind: 'single', mrp };
}

function extractAmounts(priceText) {
  const cleaned = String(priceText || '').replace(/,/g, ' ');
  if (!cleaned.trim()) return [];
  // Prefer split on dual-price separators so "500 | 700" → two options
  const parts = cleaned
    .split(/\s*(?:\||\/|\bor\b)\s*/i)
    .map((p) => p.trim())
    .filter(Boolean);
  const amounts = [];
  const pushNums = (text) => {
    const nums = String(text).match(/\d+(?:\.\d+)?/g) || [];
    for (const raw of nums) {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) amounts.push(n);
    }
  };
  if (parts.length > 1) {
    for (const part of parts) {
      const nums = String(part).match(/\d+(?:\.\d+)?/g) || [];
      if (nums.length) {
        const n = Number(nums[0]);
        if (Number.isFinite(n) && n >= 0) amounts.push(n);
      }
    }
  } else {
    pushNums(cleaned);
  }
  return amounts;
}

const SIZE_LABELS = {
  S: 'Short',
  M: 'Medium',
  L: 'Long',
  XL: 'Extra long',
  P: 'Petite',
};

function sizeLabelsFromName(name) {
  const text = String(name || '');
  const parenMulti = text.match(/\(\s*([A-Za-z]+(?:\s*[|/]\s*[A-Za-z]+)+)\s*\)/);
  if (parenMulti) {
    return parenMulti[1]
      .split(/\s*[|/]\s*/)
      .map((k) => k.trim().toUpperCase())
      .filter(Boolean);
  }
  const paren = text.match(/\(\s*([ML])\s*[|/]\s*([ML])\s*\)/i);
  if (paren) {
    return [paren[1].toUpperCase(), paren[2].toUpperCase()];
  }
  const slash = text.match(/\b([SMPL])\s*[|/]\s*([SMPL])\b/i);
  if (slash) {
    return [slash[1].toUpperCase(), slash[2].toUpperCase()];
  }
  return null;
}

function optionsFromApiPriceOptions(service) {
  const raw = service?.price_options;
  if (!Array.isArray(raw) || !raw.length) return null;
  const mapped = raw
    .map((opt, index) => {
      const amount = toNumberOrNull(opt?.amount);
      if (amount == null) return null;
      const key = String(opt?.key || `opt_${index + 1}`).trim();
      const label = String(opt?.label || '').trim();
      const displayLabel =
        label || (SIZE_LABELS[key] ? SIZE_LABELS[key] : key.replace(/_/g, ' '));
      return {
        key,
        label: displayLabel,
        amount,
        displayPrice: formatDisplayPrice(amount),
        axis: opt?.axis || 'default',
      };
    })
    .filter(Boolean);
  return mapped.length ? mapped : null;
}

/**
 * @returns {{ key: string, label: string, amount: number, displayPrice: string }[]}
 */
export function parseServicePriceOptions(service) {
  const fromApi = optionsFromApiPriceOptions(service);
  if (fromApi) return fromApi;

  const amounts = extractAmounts(service?.price);
  if (!amounts.length) {
    return [];
  }
  const sizeLabels = sizeLabelsFromName(service?.name);
  if (amounts.length === 1) {
    const amount = amounts[0];
    return [
      {
        key: 'default',
        label: '',
        amount,
        displayPrice: formatDisplayPrice(amount),
      },
    ];
  }

  return amounts.map((amount, index) => {
    let label = '';
    let key = `opt_${index + 1}`;
    if (sizeLabels && sizeLabels[index]) {
      const code = sizeLabels[index];
      label = SIZE_LABELS[code] || code;
      key = code;
    } else if (amounts.length === 2) {
      label = index === 0 ? 'Option 1' : 'Option 2';
      key = index === 0 ? 'option_1' : 'option_2';
    } else {
      label = `Option ${index + 1}`;
    }
    return {
      key,
      label,
      amount,
      displayPrice: formatDisplayPrice(amount),
    };
  });
}

/** Member + non-member row only when catalog `price_options` has both keys. */
export function getMembershipPricePair(service) {
  const raw = service?.price_options;
  if (!Array.isArray(raw) || !raw.length) return null;

  const normKey = (opt) => String(opt?.key || '').trim().toLowerCase();
  const memberRaw = raw.find((o) => normKey(o) === 'member');
  const nonMemberRaw = raw.find((o) => {
    const k = normKey(o);
    return k === 'non_member' || k === 'non-member';
  });
  if (!memberRaw || !nonMemberRaw) return null;

  const memberAmt = toNumberOrNull(memberRaw.amount);
  const nonAmt = toNumberOrNull(nonMemberRaw.amount);
  if (memberAmt == null || nonAmt == null) return null;

  const toOption = (opt, amount, fallbackLabel) => ({
    key: normKey(opt).replace('-', '_'),
    label: String(opt.label || '').trim() || fallbackLabel,
    amount,
    displayPrice: formatDisplayPrice(amount),
    axis: 'membership',
  });

  return {
    member: toOption(memberRaw, memberAmt, 'Member'),
    nonMember: toOption(nonMemberRaw, nonAmt, 'Non-member'),
  };
}

export function chipPriceLabel(service) {
  const options = parseServicePriceOptions(service);
  if (!options.length) {
    const raw = String(service?.price || '').trim();
    return raw || '';
  }
  if (options.length === 1) return options[0].displayPrice;
  const first = options[0].amount;
  const last = options[options.length - 1].amount;
  if (first === last) return formatDisplayPrice(first);
  return `₹${Number(first).toLocaleString('en-IN')}–₹${Number(last).toLocaleString('en-IN')}`;
}

export function baseServiceIdOf(item) {
  if (!item) return '';
  const base = item.base_service_id;
  if (base != null && String(base).trim()) return String(base).trim();
  const id = String(item.id || item.service_id || '').trim();
  if (id.includes('::')) return id.split('::')[0];
  return id || String(item.name || '').trim();
}

/**
 * Build selection payload for a chosen price option.
 */
export function buildServiceVariant(service, option) {
  if (!service || !option) return null;
  const baseId = String(service.id || service.name || '').trim();
  if (!baseId) return null;
  const hasLabel = Boolean(option.label);
  const multi = Boolean(option.key && option.key !== 'default');
  const nameBase = String(service.name || '').trim() || baseId;
  const name =
    hasLabel && multi ? `${nameBase} · ${option.label}` : nameBase;
  const amount = Number(option.amount);
  const entry = {
    id: multi ? `${baseId}::${option.key}` : baseId,
    base_service_id: baseId,
    name,
    price: Number.isFinite(amount) ? String(amount) : String(service.price || ''),
    amount: Number.isFinite(amount) ? amount : undefined,
  };
  if (option.key && option.key !== 'default') {
    entry.option_key = option.key;
    entry.option_label = option.label || option.key;
  }
  if (service.category) entry.category = service.category;
  if (service.description) entry.description = service.description;
  if (service.gender) entry.gender = service.gender;
  if (multi && Number.isFinite(amount)) {
    entry.net_price = amount;
    entry.sale_price = amount;
  } else {
    if (service.net_price != null && service.net_price !== '') entry.net_price = service.net_price;
    if (service.sale_price != null && service.sale_price !== '') entry.sale_price = service.sale_price;
    if (service.discount != null && service.discount !== '') entry.discount = service.discount;
  }
  return entry;
}

/** Payable unit amount for a selected catalog row (sale price when discounted). */
export function unitAmountForSelection(row, catalogService) {
  const variantAmt = variantPayableAmount(row);
  if (variantAmt != null) return variantAmt;

  const display = resolveCatalogPriceDisplay(row, catalogService);
  if (!display) {
    const options = parseServicePriceOptions(row || catalogService);
    if (options.length === 1 && Number.isFinite(options[0].amount)) return options[0].amount;
    const fromRow = toNumberOrNull(row?.amount);
    if (fromRow != null) return fromRow;
    return null;
  }
  if (display.kind === 'sale' && display.sale != null) return display.sale;
  return display.mrp;
}

function catalogLookupForRow(row, catalog) {
  const byId = new Map();
  for (const svc of catalog || []) {
    const id = String(svc?.id || '').trim();
    if (id) byId.set(id, svc);
    const base = String(svc?.id || '').split('::')[0];
    if (base && !byId.has(base)) byId.set(base, svc);
  }
  return byId.get(String(row?.id || '')) || byId.get(String(row?.base_service_id || ''));
}

/**
 * Line items + MRP / payable / savings for service-session cart modal.
 */
export function summarizeSelectedServices(selected, catalog = []) {
  const list = Array.isArray(selected) ? selected : [];
  const lines = [];
  let subtotalMrp = 0;
  let totalPayable = 0;
  let hasMoney = false;

  for (const row of list) {
    const catalogSvc = catalogLookupForRow(row, catalog);
    const qty = Math.max(1, Number(row?.quantity) || 1);
    const display = resolveCatalogPriceDisplay(row, catalogSvc);
    const unitPay = unitAmountForSelection(row, catalogSvc);
    if (unitPay == null) continue;
    hasMoney = true;
    const mrp =
      display?.mrp != null && Number.isFinite(display.mrp) ? display.mrp : unitPay;
    const lineMrp = mrp * qty;
    const linePay = unitPay * qty;
    const saved = Math.max(0, lineMrp - linePay);
    const discountPct = toNumberOrNull(row?.discount ?? catalogSvc?.discount);
    subtotalMrp += lineMrp;
    totalPayable += linePay;
    lines.push({
      id: String(row?.id || row?.name || ''),
      name: String(row?.name || catalogSvc?.name || 'Service').trim(),
      qty,
      mrp: lineMrp,
      payable: linePay,
      saved,
      discountPct: saved > 0 && discountPct != null ? discountPct : null,
    });
  }

  if (!hasMoney) {
    return { lines: [], subtotalMrp: null, totalPayable: null, totalSaved: null };
  }

  const totalSaved = Math.max(0, subtotalMrp - totalPayable);
  return {
    lines,
    subtotalMrp,
    totalPayable,
    totalSaved,
  };
}

export function sumSelectedServicesTotal(selected, catalog = []) {
  const list = Array.isArray(selected) ? selected : [];
  const byId = new Map();
  for (const svc of catalog || []) {
    const id = String(svc?.id || '').trim();
    if (id) byId.set(id, svc);
    const base = String(svc?.id || '').split('::')[0];
    if (base && !byId.has(base)) byId.set(base, svc);
  }
  let total = 0;
  let hasAmount = false;
  for (const row of list) {
    const catalogSvc = byId.get(String(row?.id || '')) || byId.get(String(row?.base_service_id || ''));
    const unit = unitAmountForSelection(row, catalogSvc);
    if (unit == null) continue;
    hasAmount = true;
    const qty = Math.max(1, Number(row?.quantity) || 1);
    total += unit * qty;
  }
  return hasAmount ? total : null;
}

export function countSelectedServiceUnits(selected) {
  return (Array.isArray(selected) ? selected : []).reduce(
    (sum, row) => sum + Math.max(1, Number(row?.quantity) || 1),
    0,
  );
}
